import { AccountBindEmailScreen } from './pages/account/AccountBindEmailScreen';
import { AccountScreen } from './pages/account/AccountScreen';
import { LoginScreen } from './pages/auth/LoginScreen';
import LoyaltyScreen from './pages/loyalty/LoyaltyScreen';
import SetupVpnScreen from './pages/setup/SetupVpnScreen';
import WelcomeScreen from './pages/welcome/WelcomeScreen';
import { useCheckSubscription } from './shared/hooks/useCheckSubscription';

import { useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { DashboardScreen } from './pages/dashboard/DashboardScreen';
import { PayHistoryScreen } from './pages/payhistory/PayHistoryScreen';
import { PaymentScreen } from './pages/payment/PaymentScreen';
import { TermsModal } from './pages/termofuse/TermModal';
import TermsOfService from './pages/termofuse/TermOfUse';
import { ErrorBoundary } from './shared/components/ErrorBoundary';
import FullScreenError from './shared/components/FullScreenError';
import FullScreenLoader from './shared/components/FullScreenLoader';
import { useTelegramBackButton } from './shared/hooks/useTelegramBackButton';
import { getTelegramWebApp, isTelegramMiniApp } from './utils/telegram';

const decodeLaunchParam = (value: string): { action?: string } => {
	const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
	const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
	return JSON.parse(atob(padded));
};

const App: React.FC = () => {
	const actionUsedRef = useRef<boolean>(false);
	const [openTelegramProxyOnDashboard, setOpenTelegramProxyOnDashboard] = useState(false);
	const telegramRuntime = isTelegramMiniApp();
	const isDevelopment = process.env.NODE_ENV === 'development';
	const [webAuthenticated, setWebAuthenticated] = useState(() => isDevelopment || Boolean(localStorage.getItem('ogo.jwt')));

	useTelegramBackButton();

	const navigate = useNavigate();

	useEffect(() => {
		if (actionUsedRef.current || !telegramRuntime) return;

		try {

			if (process.env.NODE_ENV === 'development')
				return;

			const startParam = getTelegramWebApp()?.initDataUnsafe?.start_param;
			console.log('trying to retrieve start param: ', startParam)

			if (startParam) {
				console.log('startParam: ', startParam);
				const launchParams = decodeLaunchParam(startParam);

				if (launchParams?.action) {
					switch (launchParams.action) {
						case 'dashboard':
							navigate('/dashboard');
							break;
						case 'setup':
							navigate('/setup');
							break;
						case 'payment':
							navigate('/payment');
							break;
						case 'payment_history':
							navigate('/payment_history');
							break;
						case 'loyalty':
							navigate('/loyalty');
							break;
						case 'term_of_use':
							navigate('/term_of_use');
							break;
						case 'account':
							navigate('/account');
							break;
						case 'bind_email':
							navigate('/account/bind/email');
							break;
						case 'setup_tg_proxy':
							setOpenTelegramProxyOnDashboard(true);
							navigate('/dashboard');
							break;
						default:
							navigate('/dashboard');
					}
					actionUsedRef.current = true;
				}
			}
		} catch (error) {
			console.error('Failed to parse launch params:', error);
		}
	}, [telegramRuntime, navigate]);

	const { subscription, isLoading, error, refetch } = useCheckSubscription(telegramRuntime || webAuthenticated);
	const [showTermsModal, setShowTermsModal] = useState(false);

	const onSuccessPayment = () => {
		refetch();
		navigate('/dashboard');
	}

	if (!telegramRuntime && !webAuthenticated && !isDevelopment) {
		return <LoginScreen onAuthenticated={() => setWebAuthenticated(true)} />;
	}

	if (isLoading) return <FullScreenLoader />;

	if (subscription?.hasSubscription === false) {
		if (!telegramRuntime && !isDevelopment) {
			return <FullScreenError onRetry={refetch} />;
		}
		return (
			<WelcomeScreen onSuccess={() => {
				refetch();
				navigate('/setup');
			}} />
		);
	}

	if (error)
		return <FullScreenError onRetry={refetch} />

	const renderTermsModal = showTermsModal ? (
		<TermsModal
			onAccept={() => {
				setShowTermsModal(false);
			}}
		/>
	) : null;

	const subData = subscription?.obj;

	return (
		<ErrorBoundary>
			{renderTermsModal}
			{subData && (
				<Routes>
					<Route path="/" element={<Navigate to="/dashboard" replace />} />
					<Route path="/dashboard" element={
						<DashboardScreen
							sub={subData}
							openTelegramProxyOnMount={openTelegramProxyOnDashboard}
							onTelegramProxyDialogHandled={() => setOpenTelegramProxyOnDashboard(false)}
						/>
					} />
					<Route path="/setup" element={<SetupVpnScreen />} />
					<Route path="/account" element={<AccountScreen onLogout={() => setWebAuthenticated(false)} />} />
					<Route path="/account/bind/email" element={<AccountBindEmailScreen />} />
					<Route path="/payment" element={<PaymentScreen onSuccessPayment={onSuccessPayment} />} />
					<Route path="/payment_history" element={<PayHistoryScreen />} />
					<Route path="/term_of_use" element={<TermsOfService buttonText='Назад' />} />
					<Route path="/loyalty" element={<LoyaltyScreen />} />
				</Routes>
			)}
		</ErrorBoundary>
	);

};

export default App;
